import { useParams, Link } from 'react-router-dom'
import { blogPosts } from '../../data/blog-posts'
import SEO from '../../components/SEO'
import { SITE_URL } from '../../lib/routes'
import Navbar from '../../components/Navbar'
import Footer from '../../components/Footer'
import { IconArrowLeft, IconArrowRight } from '../../components/ui/Icon'
import PostBody, { PostFigure } from './PostBody'
import TransferStrip from '../../components/ui/TransferStrip'
import { PRINT_PROCESS_I18N } from '../../data/print-process-i18n'
import PostSources from './PostSources'
import {
    BLOG_IMAGE_SIZE,
    blogCopy,
    blogIndexPath,
    blogPostPath,
    homeContactHref,
    homePath,
} from './blog-i18n'
import './blog.css'

/** The publisher and author of every post (the LocalBusiness in index.html). */
const ORGANIZATION = Object.freeze({
    '@type': 'Organization',
    '@id': `${SITE_URL}/#organization`,
    name: 'Ankaplastik',
    logo: { '@type': 'ImageObject', url: `${SITE_URL}/anka-logo-full.webp` },
})

/**
 * BlogPosting JSON-LD for one post in one locale (facts from blog-posts.js).
 *
 * @param {{ date: string, image?: string }} post
 * @param {{ title: string, excerpt: string }} content
 * @param {'tr' | 'en' | 'de'} lang
 * @param {string} slug
 * @returns {object}
 */
function blogPostingSchema(post, content, lang, slug) {
    const url = `${SITE_URL}${blogPostPath(lang, slug)}`
    return {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: content.title,
        description: content.excerpt,
        datePublished: post.date,
        inLanguage: lang,
        mainEntityOfPage: url,
        url,
        ...(post.image ? { image: `${SITE_URL}${post.image}` } : {}),
        author: ORGANIZATION,
        publisher: ORGANIZATION,
    }
}

/** Unknown slug: the page keeps the site chrome and offers the way back. */
function MissingPost({ lang, t }) {
    return (
        <>
            {/* An unknown slug is a soft 404 (the SPA answers 200): keep it out of the index. */}
            <SEO title="404" lang={lang} noindex />
            <Navbar lang={lang} />
            <main id="main-content" className="post-missing container" lang={lang}>
                <h1>404</h1>
                <Link to={blogIndexPath(lang)} className="btn btn--ghost" viewTransition>
                    <IconArrowLeft />
                    {t.back}
                </Link>
            </main>
            <Footer lang={lang} />
        </>
    )
}

export default function BlogPost({ lang = 'tr' }) {
    const { slug } = useParams()
    const t = blogCopy(lang)
    const post = blogPosts.find((p) => p.slug === slug)

    if (!post) return <MissingPost lang={lang} t={t} />

    const content = post.languages[lang]

    return (
        <>
            <SEO
                title={content.title}
                description={content.excerpt}
                lang={lang}
                url={blogPostPath(lang, slug)}
                ogType="article"
                ogImage={post.image}
                jsonLd={blogPostingSchema(post, content, lang, slug)}
            />

            <Navbar lang={lang} />

            <main id="main-content" lang={lang}>
                <article className="post">
                    {/* Reading progress: a CSS view timeline on the body, no JS. */}
                    <div className="post-progress" aria-hidden="true" />

                    <div className="post-grid container">
                        <header className="post-head">
                            <nav className="post-crumbs" aria-label={t.breadcrumb}>
                                <ol>
                                    <li>
                                        <Link to={homePath(lang)} className="legend" viewTransition>
                                            {t.home}
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to={blogIndexPath(lang)} className="legend" viewTransition>
                                            {t.title}
                                        </Link>
                                    </li>
                                </ol>
                            </nav>
                            <h1 className="post-title">{content.title}</h1>
                            <p className="post-meta">
                                <time className="legend tabular" dateTime={post.date}>
                                    {post.date}
                                </time>
                            </p>
                        </header>

                        {post.artwork ? (
                            <div className="post-cover">
                                <TransferStrip
                                    labels={(PRINT_PROCESS_I18N[post.artwork]?.[lang] ?? PRINT_PROCESS_I18N.pad.tr).stations.map((s) => s.name)}
                                />
                            </div>
                        ) : post.image ? (
                            <div className="post-cover plate plate--marks">
                                <img
                                    src={post.image}
                                    alt={content.title}
                                    width={BLOG_IMAGE_SIZE.width}
                                    height={BLOG_IMAGE_SIZE.height}
                                    fetchPriority="high"
                                    decoding="async"
                                />
                            </div>
                        ) : null}

                        <PostBody markdown={content.content} />

                        {/* The cover infographic is lettered in Turkish: on the TR post it
                            closes the article as a summary figure; EN/DE lead with the artwork. */}
                        {post.artwork && post.image && lang === 'tr' ? (
                            <PostFigure src={post.image} alt={content.title} />
                        ) : null}

                        <PostSources label={t.sources} sources={content.sources} />

                        <footer className="post-end">
                            <Link to={blogIndexPath(lang)} className="link-arrow post-end-back" viewTransition>
                                <IconArrowLeft />
                                {t.back}
                            </Link>
                            <a href={homeContactHref(lang)} className="link-arrow post-end-rfq">
                                {t.rfq}
                                <IconArrowRight />
                            </a>
                        </footer>
                    </div>
                </article>
            </main>

            <Footer lang={lang} />
        </>
    )
}
